import gql from 'graphql-tag';

import type { AllInstructionsData } from 'src/data/generation';
import { executeGqlMutation } from 'src/graphql/client';
import { getSingleProp } from 'src/lib/graphql';
import type { Override } from 'src/lib/types';
import type {
  BackendJob,
  BackendMutationImproveShortFormAssetsArgs,
  BackendSectionInput,
} from 'src/types/backend';

// The structure instructions travel as a JSON scalar, which the panel holds in one shape.
type ImproveSection = Override<
  BackendSectionInput,
  { structureInstructionsData?: AllInstructionsData }
>;

type Variables = Override<
  BackendMutationImproveShortFormAssetsArgs,
  { sections: ImproveSection[]; structureInstructionsData?: AllInstructionsData }
>;

type FeedJob = Pick<BackendJob, 'id' | 'status'>;

interface Options {
  abortSignal?: AbortSignal;
}

const MUTATION = gql`
  mutation ImproveShortFormAssets(
    $projectId: ID!
    $sectionId: ID!
    $generationTool: GenerationToolEnum
    $sections: [SectionInput!]!
    $keywords: [String!]
    $variationCount: Int!
    $language: String!
    $languageTargetFormality: LanguageTargetFormalityEnum
    $contentType: ProjectContentTypeEnum
    $outlineJobId: ID
    $recomposeMode: String
    $onlyImproveScore: Boolean
    $manualInstructions: String
    $tovInput: TovInput
    $structureInstructionsData: JSON
  ) {
    improveShortFormAssets(
      projectId: $projectId
      sectionId: $sectionId
      generationTool: $generationTool
      sections: $sections
      keywords: $keywords
      variationCount: $variationCount
      language: $language
      languageTargetFormality: $languageTargetFormality
      contentType: $contentType
      outlineJobId: $outlineJobId
      recomposeMode: $recomposeMode
      onlyImproveScore: $onlyImproveScore
      manualInstructions: $manualInstructions
      tovInput: $tovInput
      structureInstructionsData: $structureInstructionsData
    ) {
      id
      status
    }
  }
`;

/**
 * The sections-feed counterpart of `improveShortFormSections`, differing only by
 * taking a `sectionId` — which is what gives the result somewhere to land.
 *
 * A long-form content type is improved into one long-form card instead, from the `sections` it is
 * given or from the parts of an outline job — never both, so the outline branch sends no sections.
 */
export const improveShortFormAssets = async (
  variables: Variables,
  options: Options | null = null
): Promise<FeedJob | null> => {
  const response = await executeGqlMutation({
    mutation: MUTATION,
    variables,
    options: {
      abortSignal: options?.abortSignal,
      traceAcRequests: true,
    },
  });

  return getSingleProp<FeedJob>(response.data) ?? null;
};
